import { motion } from 'framer-motion';
import { MapPin, Search, Sparkles, Star, TrendingUp } from 'lucide-react';
import { PrimaryCTA, SecondaryCTA } from '@/components/PrimaryCTA';
import heroBg from '@/assets/hero-blueprint-bg.webp';

// Simple line chart component
function PerformanceChart() {
  return (
    <svg viewBox="0 0 200 80" className="w-full h-20">
      {/* Grid lines */}
      <defs>
        <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="hsl(142 76% 36%)" stopOpacity="0.6" />
          <stop offset="100%" stopColor="hsl(142 76% 36%)" stopOpacity="0" />
        </linearGradient>
      </defs>
      
      {/* Area fill */}
      <path
        d="M 0 70 L 20 65 L 40 60 L 60 55 L 80 45 L 100 40 L 120 35 L 140 25 L 160 20 L 180 15 L 200 10 L 200 80 L 0 80 Z"
        fill="url(#chartGradient)"
      />
      
      {/* Line */}
      <motion.path
        d="M 0 70 L 20 65 L 40 60 L 60 55 L 80 45 L 100 40 L 120 35 L 140 25 L 160 20 L 180 15 L 200 10"
        fill="none"
        stroke="hsl(142 76% 36%)"
        strokeWidth="2.5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, ease: "easeOut", delay: 0.5 }}
      />
      
      {/* Data points */}
      {[
        { x: 80, y: 45 },
        { x: 140, y: 25 },
        { x: 200, y: 10 }
      ].map((point, i) => (
        <motion.circle
          key={i}
          cx={point.x}
          cy={point.y}
          r="4"
          fill="hsl(142 76% 36%)"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.5 + i * 0.2 }}
        />
      ))}
    </svg>
  );
}

// Gauge component
function ScoreGauge({ score, label, delay, displayText }: { score: number; label: string; delay: number; displayText?: string }) {
  const percentage = score;
  const circumference = 2 * Math.PI * 18;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <motion.div 
      className="flex flex-col items-center gap-1"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.5 }}
    >
      <div className="relative w-12 h-12">
        <svg className="w-12 h-12 -rotate-90">
          <circle
            cx="24"
            cy="24"
            r="18"
            stroke="hsl(217 33% 25%)"
            strokeWidth="3"
            fill="none"
          />
          <motion.circle
            cx="24"
            cy="24"
            r="18"
            stroke="hsl(142 76% 36%)"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ delay: delay + 0.3, duration: 1, ease: "easeOut" }}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-foreground">
          {displayText || score}
        </span>
      </div>
      <span className="text-[10px] text-muted-foreground text-center leading-tight">{label}</span>
    </motion.div>
  );
}

export function Hero() {
  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden"
      aria-label="Hero section showcasing high-performance AI-ready web design for South Wales trades"
    >
      {/* Background Image - LCP Optimized */}
      <img 
        src={heroBg}
        alt=""
        loading="eager"
        fetchPriority="high"
        decoding="async"
        sizes="100vw"
        className="absolute inset-0 z-0 w-full h-full object-cover object-center"
      />
      
      {/* Dark Gradient Overlay for Text Readability */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#001f3f]/55 via-[#001f3f]/50 to-background" />
      
      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          {/* Left Side - Text Content */}
          <div className="text-left">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center lg:justify-start mb-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
                Now AI-Ready for 2026
              </div>
            </motion.div>
            
            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="font-display text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold leading-[1.1] tracking-tight mb-6"
            >
              <span className="text-foreground">High-Performance Websites for</span>
              <br />
              <span className="text-foreground">South Wales Trades –</span>
              <br />
              <span className="text-gradient-cyan">Rank High & Generate Leads</span>
            </motion.h1>
            
            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-xl leading-relaxed mb-8"
            >
              Stop losing jobs to slow websites. We build{' '}
              <span className="text-foreground font-medium">lightning fast sites with 90+ PageSpeed scores</span>, tuned for Siri, ChatGPT and voice search.
              <br className="hidden sm:block" /><br className="hidden sm:block" />
              Based in North Cornelly, we serve trades across Cardiff, Swansea, Newport, Bridgend and the M4 corridor, turning local searches into booked jobs.{' '}
              <span className="text-orange-400 font-medium">Content and SEO included with zero agency nonsense.</span>
            </motion.p>
            
            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <PrimaryCTA 
                text="Get Your Free Site Audit" 
                href="/contact" 
                external={false}
                className="min-h-[44px]"
              />
              <SecondaryCTA 
                text="Run Your Free Mobile Speed Check"
                href="https://pagespeed.web.dev/"
                className="min-h-[44px]"
              />
            </motion.div>
            
            {/* 5-Star Rating Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col items-center lg:items-start gap-4"
            >
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 bg-white/5">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-foreground font-medium">5-Star Rated</span>
              </div>
              
              {/* Platform Icons */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">Google Maps</span>
                </div>
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  <span className="text-sm">Google Search</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">AI Search</span>
                </div>
              </div>
              
              {/* Trust Line */}
              <p className="text-sm text-slate-400 max-w-xl text-center lg:text-left">
                Trusted by trades across South Wales, from Porthcawl, Cornelly and Kenfig Hill to Barry, Newport, Neath and the Valleys.
              </p>
            </motion.div>
          </div>
          
          {/* Right Side - 3D Glassmorphism Card */}
          <motion.div
            initial={{ opacity: 0, x: 50, rotateY: -15 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
            className="relative hidden lg:block"
          >
            {/* Orange Glow Behind Card */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div 
                className="w-[400px] h-[400px] rounded-full bg-orange-500/20 blur-[100px]"
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3] 
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
              />
            </div>
            
            {/* 3D Tilted Glass Card */}
            <motion.div
              className="relative"
              style={{ 
                perspective: '1000px',
                transformStyle: 'preserve-3d'
              }}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <div 
                className="relative glass-card p-6 rounded-3xl border border-white/20"
                style={{ 
                  transform: 'rotateY(-8deg) rotateX(5deg)',
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* Card Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Performance Dashboard</p>
                      <p className="text-xs text-muted-foreground">Real-time PageSpeed Insights</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <span className="text-xs text-green-400">Live</span>
                  </div>
                </div>
                
                {/* Chart Section */}
                <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-muted-foreground">Traffic Growth</span>
                    <span className="text-xs text-green-400 font-medium">+127% this month</span>
                  </div>
                  <PerformanceChart />
                </div>
                
                {/* Gauges */}
                <div className="grid grid-cols-4 gap-2">
                  <ScoreGauge score={90} label="Performance" delay={0.6} displayText="90+" />
                  <ScoreGauge score={100} label="Accessibility" delay={0.7} />
                  <ScoreGauge score={100} label="Best Practices" delay={0.8} />
                  <ScoreGauge score={100} label="SEO" delay={0.9} />
                </div>
                
                {/* Bottom Stats */}
                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-green-400">£12,450</p>
                    <p className="text-xs text-muted-foreground">Revenue from Leads</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-foreground">47</p>
                    <p className="text-xs text-muted-foreground">Verified Google Reviews</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
